/**
 * The push state machine, rendered honestly.
 *
 * `adforge/schemas/enums.py` (W0) owns `PushState` and `ALLOWED_TRANSITIONS`; the
 * console mirrors the transition table only to answer "what can happen next", never to
 * invent a transition of its own. `live_paused` is the terminal success state in v1 —
 * the UI must never imply a campaign is serving (04-contracts.md §6, 00-product-brief.md).
 */
import type { PushState } from '@/api/types'

export const ALL_PUSH_STATES: readonly PushState[] = [
  'draft',
  'preflight',
  'blocked',
  'dry_run',
  'needs_fix',
  'queued_for_quota',
  'publishing_pages',
  'pushing',
  'reversing',
  'reversed',
  'live_paused',
  'failed',
] as const

/** Mirror of `ALLOWED_TRANSITIONS` in adforge/schemas/enums.py. */
export const ALLOWED_TRANSITIONS: Record<PushState, readonly PushState[]> = {
  draft: ['preflight'],
  preflight: ['dry_run', 'blocked'],
  blocked: ['preflight'], // recoverable: fix conversion tracking / billing, re-run preflight
  dry_run: ['publishing_pages', 'needs_fix', 'queued_for_quota'],
  needs_fix: ['dry_run'],
  queued_for_quota: ['publishing_pages', 'dry_run'],
  publishing_pages: ['pushing', 'failed'],
  pushing: ['live_paused', 'reversing', 'failed'],
  reversing: ['reversed', 'failed'],
  live_paused: ['reversing'],
  failed: ['reversing'],
  reversed: [],
}

export type StateTone = 'neutral' | 'running' | 'attention' | 'danger' | 'success'

export interface StateCopy {
  label: string
  /** Plain English for a paying customer. */
  detail: string
  tone: StateTone
  terminal: boolean
  /** True when a human must act for this state to move on. */
  needsUser: boolean
}

export const STATE_COPY: Record<PushState, StateCopy> = {
  draft: {
    label: 'Draft',
    detail: 'Nothing has been sent to the platform yet.',
    tone: 'neutral',
    terminal: false,
    needsUser: false,
  },
  preflight: {
    label: 'Preflight',
    detail:
      'Checking the account can receive a campaign at all — conversion tracking, billing, account status.',
    tone: 'running',
    terminal: false,
    needsUser: false,
  },
  blocked: {
    label: 'Blocked',
    detail:
      'The account failed preflight. AdForge refuses to push into an account with no conversion action configured — that is the $300k-no-tracking failure mode. Fix the account, then re-run preflight.',
    tone: 'danger',
    terminal: false,
    needsUser: true,
  },
  dry_run: {
    label: 'Dry run',
    detail:
      "The platform's own validate_only run. Errors here are zero-cost findings, and nothing is pushed while any error stands.",
    tone: 'running',
    terminal: false,
    needsUser: false,
  },
  needs_fix: {
    label: 'Needs fix',
    detail:
      'The dry run found errors. Edit the flagged assets and re-run the dry run — the push stays refused until they are clean.',
    tone: 'attention',
    terminal: false,
    needsUser: true,
  },
  queued_for_quota: {
    label: 'Queued for quota',
    detail:
      "Today's operation quota cannot cover this push yet. It retries automatically once a lease is granted; the retry-after time is shown below.",
    tone: 'attention',
    terminal: false,
    needsUser: false,
  },
  publishing_pages: {
    label: 'Publishing landing pages',
    detail:
      'Landing pages deploy first, on purpose: a campaign pointing at a 404 final URL gets disapproved.',
    tone: 'running',
    terminal: false,
    needsUser: false,
  },
  pushing: {
    label: 'Pushing campaigns',
    detail: 'Campaign entities are being written to the platform.',
    tone: 'running',
    terminal: false,
    needsUser: false,
  },
  reversing: {
    label: 'Reversing',
    detail:
      'The stored reversal plan is running: everything AdForge created is being removed, children before parents.',
    tone: 'running',
    terminal: false,
    needsUser: false,
  },
  reversed: {
    label: 'Reversed',
    detail:
      'The push was undone. The account is back to its prior condition; nothing AdForge created remains.',
    tone: 'neutral',
    terminal: true,
    needsUser: false,
  },
  live_paused: {
    label: 'Live — paused',
    detail:
      'Terminal success state. The campaigns exist in the account and are PAUSED. Nothing is serving until a human unpauses it in the platform UI. AdForge never unpauses for you.',
    tone: 'success',
    terminal: true,
    needsUser: true,
  },
  failed: {
    label: 'Failed',
    detail:
      'The push stopped. Whatever was created can be removed with the stored reversal plan — undo is a feature, not a support ticket.',
    tone: 'danger',
    terminal: false,
    needsUser: true,
  },
}

/** The canonical ladder, in the order a healthy push walks it. */
export const PUSH_LADDER: readonly PushState[] = [
  'draft',
  'preflight',
  'dry_run',
  'publishing_pages',
  'pushing',
  'live_paused',
] as const

export type LadderStepStatus = 'done' | 'current' | 'failed' | 'skipped' | 'pending'

export interface LadderStep {
  state: PushState
  status: LadderStepStatus
}

/**
 * Where the current state sits on the ladder. Branch states (`blocked`, `needs_fix`,
 * `queued_for_quota`, `reversing`, `reversed`, `failed`) map onto the step they
 * interrupted, so the user sees *where* the push stopped, not just that it stopped.
 */
export function ladderFor(current: PushState): LadderStep[] {
  const interruptedStep: Partial<Record<PushState, PushState>> = {
    blocked: 'preflight',
    needs_fix: 'dry_run',
    queued_for_quota: 'dry_run',
    reversing: 'pushing',
    reversed: 'pushing',
    failed: 'pushing',
  }
  const branch = interruptedStep[current]
  const currentIndex = PUSH_LADDER.indexOf(branch ?? current)
  return PUSH_LADDER.map((state, index) => {
    let status: LadderStepStatus
    if (branch && state === branch) status = 'failed'
    else if (index < currentIndex) status = 'done'
    else if (index === currentIndex) status = 'current'
    else status = 'pending'
    return { state, status }
  })
}

export function nextStates(current: PushState): readonly PushState[] {
  return ALLOWED_TRANSITIONS[current] ?? []
}

/** A push that cannot move without a human: the UI shows the action to take. */
export function isTerminal(current: PushState): boolean {
  return STATE_COPY[current].terminal
}

export function isHealthyEnd(current: PushState): boolean {
  return current === 'live_paused'
}

/**
 * Where the one action AdForge deliberately refuses to take happens. v1 creates
 * campaigns paused and never unpauses them (00-product-brief.md guardrails), so the UI
 * always names the place a human does it.
 */
export const HUMAN_UNPAUSE_LOCATION = 'the Google Ads or Microsoft Advertising UI'
